// SubmarkerOverlay — a minimal `google.maps.OverlayView` that replaces the archived InfoBox utility
// for the online-app submarker. It provides only the InfoBox surface the submarker uses: place a
// content element at a marker anchor (bottom-aligned, with a pixel offset) in the floatPane, keep it
// anchored on map moves, and auto-pan the map once so the box is visible. The pill->card morph and
// transform-centring live in the submarker component + `_submarker.scss`; this class only positions
// the wrapper (it sets `left`/`bottom` and never a positioning transform, exactly as InfoBox did).
//
// Ports the relevant bits of `public/javascripts/online/apps/infobox.js` (draw + panBox_).
//
// Defined via a lazy, memoised factory because `class extends google.maps.OverlayView` evaluates its
// superclass at definition time, and `google.maps` isn't loaded when this module is first imported —
// only when the submarker is constructed (after the map libraries load). Callers use
// `getSubmarkerOverlayClass()` at instantiation time.

let SubmarkerOverlayClass = null;

export function getSubmarkerOverlayClass() {
    if (SubmarkerOverlayClass) return SubmarkerOverlayClass;

    SubmarkerOverlayClass = class SubmarkerOverlay extends google.maps.OverlayView {

        // options: { pixelOffset: google.maps.Size, infoBoxClearance: google.maps.Size, disableAutoPan: bool }
        // (alignBottom is always true for the submarker, so it's implicit here.)
        constructor(options = {}) {
            super();
            this.contentElement = null;
            this.position = null;
            this.pixelOffset = options.pixelOffset || new google.maps.Size(0, 0);
            this.infoBoxClearance = options.infoBoxClearance || new google.maps.Size(0, 0);
            this.disableAutoPan = !!options.disableAutoPan;
            this.container = null;
        }

        // --- OverlayView lifecycle ---

        onAdd() {
            // The container is created lazily on first draw() (mirrors InfoBox.createInfoBoxDiv_), so
            // nothing to do here beyond the base add.
        }

        draw() {
            const projection = this.getProjection();
            if (!projection || !this.position) return;

            let firstDraw = false;
            if (!this.container) {
                this.container = document.createElement('div');
                this.container.style.position = 'absolute';
                if (this.contentElement) this.container.appendChild(this.contentElement);
                this.getPanes().floatPane.appendChild(this.container);
                // Contain the card's events so a tap on it doesn't also register as a tap on the map
                // behind it (which would fire `mapClicked` → deselect + flip the URL back to the filter).
                // This is the OverlayView API for exactly this — it stops clicks AND gestures
                // (drag/scroll on the card no longer pans the map), restoring the event containment the
                // archived InfoBox utility provided.
                SubmarkerOverlay.preventMapHitsAndGesturesFrom(this.container);
                firstDraw = true;
            }

            // Same positioning maths as InfoBox.draw() with alignBottom: the wrapper's bottom edge sits
            // at the marker pixel (+ vertical offset), and its left edge at the marker pixel
            // (+ horizontal offset). The wrapper centres itself horizontally via `translateX(-50%)`.
            const pix = projection.fromLatLngToDivPixel(this.position);
            this.container.style.left = (pix.x + this.pixelOffset.width) + 'px';
            this.container.style.bottom = -(pix.y + this.pixelOffset.height) + 'px';

            // InfoBox pans only when the div is first created (inside createInfoBoxDiv_), not on every
            // redraw — so a map move/zoom just re-anchors the box, and only the initial open pans.
            if (firstDraw) this.panToKeepVisible();
        }

        onRemove() {
            if (this.container && this.container.parentNode) {
                this.container.parentNode.removeChild(this.container);
            }
            this.container = null;
        }

        // --- InfoBox-compatible interface used by OnlineAppSubmarker ---

        setContent(element) {
            this.contentElement = element;
            if (this.container) {
                // Re-place the content (matches InfoBox rewriting its container via innerHTML +
                // appendChild). Re-appending resets any running CSS transition on the element, which is
                // why the submarker only calls this once the morph has settled.
                while (this.container.firstChild) this.container.removeChild(this.container.firstChild);
                this.container.appendChild(element);
                this.draw();
            }
        }

        setOptions(options = {}) {
            if (options.pixelOffset) this.pixelOffset = options.pixelOffset;
            if (options.infoBoxClearance) this.infoBoxClearance = options.infoBoxClearance;
            if (typeof options.disableAutoPan !== 'undefined') this.disableAutoPan = !!options.disableAutoPan;
            if (this.container) this.draw(); // reposition; no re-pan (matches InfoBox.setOptions -> draw)
        }

        open(map, anchor) {
            // AdvancedMarkerElement exposes `.position` (a LatLng or LatLngLiteral); both are accepted by
            // fromLatLngToDivPixel / getBounds().contains / new LatLngBounds() / setCenter below.
            this.position = anchor.position;
            this.setMap(map);
        }

        close() {
            this.setMap(null);
        }

        // Auto-pan (ports infobox.js panBox_ for the alignBottom case): pan via panToBounds with padding
        // so the whole box is visible with the configured clearance.
        panToKeepVisible() {
            if (this.disableAutoPan) return;
            const map = this.getMap();
            if (!(map instanceof google.maps.Map) || !this.container || !this.position) return;

            if (!map.getBounds().contains(this.position)) {
                map.setCenter(this.position);
            }

            const offX = this.pixelOffset.width;
            const offY = this.pixelOffset.height;
            const boxWidth = this.container.offsetWidth;
            const boxHeight = this.container.offsetHeight;
            const padX = this.infoBoxClearance.width;
            const padY = this.infoBoxClearance.height;

            // alignBottom padding (from panBox_): reserve the box height above the anchor.
            const padding = {
                left: -offX + padX,
                right: offX + boxWidth + padX,
                top: -offY + padY + boxHeight,
                bottom: offY + padY,
            };
            map.panToBounds(new google.maps.LatLngBounds(this.position), padding);
        }
    };

    return SubmarkerOverlayClass;
}
